import React from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import {
  TrendingUp,
  Group,
  BrandingWatermark,
  Campaign,
  Analytics,
  CheckCircleOutline,
} from "@mui/icons-material";

export default function MarketingDetails() {
  const services = [
    {
      title: "Strategic Marketing Planning",
      icon: <TrendingUp sx={{ fontSize: 40, color: "primary.main" }} />,
      description:
        "Develop comprehensive marketing strategies tailored to your business goals and target audience.",
      benefits: [
        "Custom marketing roadmap",
        "Competitive analysis",
        "Market research and insights",
        "ROI-focused planning",
      ],
    },
    {
      title: "Brand Development",
      icon: <BrandingWatermark sx={{ fontSize: 40, color: "primary.main" }} />,
      description:
        "Create and strengthen your brand identity to stand out in the market.",
      benefits: [
        "Brand identity design",
        "Brand voice development",
        "Visual branding guidelines",
        "Brand positioning strategy",
      ],
    },
    {
      title: "Social Media Management",
      icon: <Group sx={{ fontSize: 40, color: "primary.main" }} />,
      description:
        "Engage your audience and build community through effective social media strategies.",
      benefits: [
        "Content calendar planning",
        "Community engagement",
        "Platform-specific strategies",
        "Social media analytics",
      ],
    },
    {
      title: "Content Marketing",
      icon: <Campaign sx={{ fontSize: 40, color: "primary.main" }} />,
      description:
        "Create valuable content that attracts and retains your target audience.",
      benefits: [
        "Content strategy development",
        "SEO-optimized writing",
        "Multi-format content creation",
        "Content performance tracking",
      ],
    },
    {
      title: "Marketing Analytics",
      icon: <Analytics sx={{ fontSize: 40, color: "primary.main" }} />,
      description:
        "Track and analyze marketing performance to optimize your strategies.",
      benefits: [
        "Data-driven insights",
        "Performance reporting",
        "Campaign optimization",
        "ROI tracking",
      ],
    },
  ];

  return (
    <Box component="section" sx={{ py: 8, bgcolor: "#f5f5f5" }}>
      <Container maxWidth="lg">
        {/* Section Header */}
        <Box sx={{ mb: 8, textAlign: "center" }}>
          <Typography
            component="h1"
            variant="h2"
            sx={{
              fontWeight: 600,
              fontFamily: "Nunito, serif",
              mb: 2,
              color: "text.primary",
            }}
          >
            Our Marketing Solutions
          </Typography>
          <Typography
            variant="h5"
            sx={{
              fontFamily: "Nunito, serif",
              color: "text.secondary",
              maxWidth: 800,
              mx: "auto",
            }}
          >
            Discover how our comprehensive marketing services can help your business grow and succeed in today's digital landscape.
          </Typography>
        </Box>

        {/* Services Grid */}
        <Grid container spacing={4}>
          {services.map((service, index) => (
            <Grid item xs={12} md={6} key={index}>
              <Card
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  transition: "transform 0.3s, box-shadow 0.3s",
                  "&:hover": {
                    transform: "translateY(-5px)",
                    boxShadow: 3,
                  },
                }}
              >
                <CardContent>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    {service.icon}
                    <Typography
                      component="h2"
                      variant="h5"
                      sx={{
                        ml: 2,
                        fontFamily: "Nunito, serif",
                        fontWeight: 600,
                      }}
                    >
                      {service.title}
                    </Typography>
                  </Box>

                  <Typography
                    variant="body1"
                    sx={{
                      mb: 3,
                      color: "text.secondary",
                      fontFamily: "Nunito, serif",
                    }}
                  >
                    {service.description}
                  </Typography>

                  <List disablePadding>
                    {service.benefits.map((benefit, idx) => (
                      <ListItem key={idx} sx={{ p: 0, mb: 1 }}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <CheckCircleOutline sx={{ color: "primary.main", fontSize: 20 }} />
                        </ListItemIcon>
                        <ListItemText
                          primary={benefit}
                          sx={{
                            "& .MuiListItemText-primary": {
                              fontFamily: "Nunito, serif",
                            },
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
}
